// The Analyst copilot sidecar.
//
// One conversation, two surfaces. On /analyst the chat owns the whole
// content area (AnalystPage). Everywhere ELSE in the shell this dock
// keeps the same conversation reachable: docked to the right edge as a
// narrow panel, or collapsed into a floating pill. All chat state lives
// in Redux, so the thread survives moving between surfaces.
//
// Why it exists: the chat creates real things - goals, alerts, segments -
// that live on real pages. The dock lets the user GO to those pages
// (artifact cards deep-link) without losing the conversation, and walks
// them there itself on the big milestone (first goal serving).
//
// Layout: on wide viewports (>= 1440px) the open dock PUSHES the page
// content (ContentBody adds right padding); below that it overlays with
// a shadow. Width is drag-resizable and persisted, as is open/collapsed.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, IconButton, Stack, Tooltip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { keyframes } from '@mui/system';
import {
  AutoAwesome as SparkIcon,
  OpenInFull as ExpandIcon,
  KeyboardDoubleArrowRight as CollapseIcon,
} from '@mui/icons-material';

import AskDataPanel from '../signals/askData/AskDataPanel';
import { routeForArtifact } from '../signals/askData/ArtifactCard';
import { surfacesFor } from '../signals/askData/uiTokens';
import useRealtimeRefresh from '../../realtime/useRealtimeRefresh';
import { hasUiFlag, setUiFlag } from '../../utils/uiFlags';
import { useAnalystDock } from './dockState';

// State lives in dockState.js (so chat-level components like the
// artifact cards can open the dock too); re-exported here so the shell
// keeps a single import site.
export { AnalystDockProvider, ContentBody, useAnalystDock } from './dockState';

const HEADER_H = 56;   // .header fixed height


// ---------------------------------------------------------------------------
// The dock itself.
// ---------------------------------------------------------------------------

const pulse = keyframes`
  0%, 100% { transform: scale(1);   opacity: 1; }
  50%      { transform: scale(1.5); opacity: 0.55; }
`;

// Collapsed pill entrance - a soft pop instead of an instant swap.
const pillIn = keyframes`
  from { opacity: 0; transform: scale(0.82) translateY(8px); }
  to   { opacity: 1; transform: scale(1)    translateY(0); }
`;

// Slow attention ring around the collapsed pill (color = currentColor of
// the pseudo-element, so it stays on-brand in both modes).
const ringPulse = keyframes`
  0%   { box-shadow: 0 0 0 0 currentColor; opacity: 0.5; }
  70%  { box-shadow: 0 0 0 11px currentColor; opacity: 0; }
  100% { box-shadow: 0 0 0 0 currentColor; opacity: 0; }
`;

// First-run hint bubbles pop in with a little overshoot.
const bubbleIn = keyframes`
  from { opacity: 0; transform: translateY(6px) scale(0.92); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

// One-time coach bubble. High-contrast on purpose (brand fill, dark/white
// text) - it exists to be noticed once, then never again.
const HintBubble = ({ children, caret, onClick, sx }) => (
  <Box
    onClick={onClick}
    sx={(t) => {
      const dark = t.palette.mode === 'dark';
      const fill = dark ? (t.brand?.lime || '#c0fb6e') : (t.brand?.teal || t.palette.primary.main);
      return {
        position: 'absolute',
        zIndex: 3,
        maxWidth: 230,
        px: 1.5, py: 1,
        borderRadius: '12px',
        bgcolor: fill,
        color: dark ? '#101708' : '#ffffff',
        fontSize: 12.5, fontWeight: 600, lineHeight: 1.45,
        letterSpacing: '0.01em',
        cursor: 'pointer',
        boxShadow: `0 10px 30px ${alpha(fill, 0.35)}, 0 4px 14px rgba(0,0,0,0.3)`,
        animation: `${bubbleIn} 240ms cubic-bezier(0.34, 1.3, 0.44, 1) both`,
        '&::after': {
          content: '""',
          position: 'absolute',
          width: 10, height: 10,
          bgcolor: fill,
          transform: 'rotate(45deg)',
          ...(caret === 'top-right' ? { top: -4, right: 20 } : { bottom: -4, right: 20 }),
        },
        ...sx,
      };
    }}
  >
    {children}
  </Box>
);

// One curve for every dock motion (panel slide + content push) so the
// morph reads as a single gesture.
const DOCK_EASE = 'cubic-bezier(0.32, 0.72, 0.24, 1)';

// Collect the goal ids this conversation created (from artifact cards),
// so realtime goal.status events can be attributed to the chat.
const chatGoalIdsOf = (messages) => {
  const ids = new Set();
  (messages || []).forEach((m) => {
    (m?.blocks || []).forEach((b) => {
      if (
        b?.type === 'attachment' && b.kind === 'artifact'
        && b.spec?.artifact_type === 'goal' && b.spec?.id
      ) {
        ids.add(String(b.spec.id));
      }
    });
  });
  return ids;
};

// Never yank navigation out from under someone mid-thought.
const userIsTyping = () => {
  const el = document.activeElement;
  if (!el) return false;
  const tag = (el.tagName || '').toUpperCase();
  return (tag === 'TEXTAREA' || tag === 'INPUT') && Boolean(el.value);
};

const AnalystDock = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    open, width, openDock, collapseDock, setWidth, dockActive, isMobile,
  } = useAnalystDock();

  const isLoading = useSelector((s) => s.signal_ask_data?.isLoading || false);
  const messages = useSelector((s) => s.signal_ask_data?.messages || []);
  const lastMilestone = useSelector((s) => s.signal_ask_data?.lastMilestone || null);

  const onAnalyst = location.pathname === '/analyst';

  // -- one-time hint bubbles (account-scoped ui_flags) ---------------------
  // Each shows exactly once, ever: the flag is set the moment it appears.
  const [dockHint, setDockHint] = useState(false);
  const [pillHint, setPillHint] = useState(false);
  const dockHintTimer = useRef(null);
  const pillHintTimer = useRef(null);
  useEffect(() => () => {
    clearTimeout(dockHintTimer.current);
    clearTimeout(pillHintTimer.current);
  }, []);

  // First time the app walks the user somewhere and docks the chat:
  // point out the way back to the full conversation.
  const maybeDockHint = useCallback(() => {
    if (hasUiFlag('dock_hint_seen')) return;
    setUiFlag('dock_hint_seen');
    setDockHint(true);
    clearTimeout(dockHintTimer.current);
    dockHintTimer.current = setTimeout(() => setDockHint(false), 9000);
  }, []);

  // First time the user collapses the chat into the pill: show where it
  // went and how to bring it back.
  const maybePillHint = useCallback(() => {
    if (hasUiFlag('pill_hint_seen')) return;
    setUiFlag('pill_hint_seen');
    setPillHint(true);
    clearTimeout(pillHintTimer.current);
    pillHintTimer.current = setTimeout(() => setPillHint(false), 9000);
  }, []);

  // -- auto-open when leaving /analyst mid-answer -------------------------
  // Navigating away while the model is still working shouldn't hide the
  // answer: the dock opens so the stream stays visible. Phones skip
  // this - the sheet would cover the page they just went to; the pill's
  // pulse shows the answer is still streaming.
  const prevPathRef = useRef(location.pathname);
  useEffect(() => {
    const prev = prevPathRef.current;
    prevPathRef.current = location.pathname;
    if (prev === '/analyst' && location.pathname !== '/analyst' && isLoading && !isMobile) {
      openDock();
    }
  }, [location.pathname, isLoading, openDock, isMobile]);

  // -- milestone handoff: chat tool call saw the goal go live -------------
  // Only from /analyst (elsewhere the user already chose where to be),
  // and never while they're typing. The chat full-screen -> goal page +
  // docked chat transition IS the graduation from setup to product.
  const handledMilestoneTs = useRef(0);
  useEffect(() => {
    if (!lastMilestone || lastMilestone.ts === handledMilestoneTs.current) return;
    handledMilestoneTs.current = lastMilestone.ts;
    if (!onAnalyst || userIsTyping()) return;
    const route = routeForArtifact(lastMilestone.spec);
    if (!route) return;
    // Phones land on the page with the chat minimized to the pill;
    // desktop docks the conversation beside it.
    if (!isMobile) openDock();
    navigate(route);
  }, [lastMilestone, onAnalyst, openDock, navigate, isMobile]);

  // NOTE: show_page (page_tour attachments) deliberately does NOT
  // navigate or dock anything. It leaves a clickable page card in the
  // thread and a bubble on the sidebar item (see Sidebar.js) - the user
  // decides whether to go. Only the goal-serving milestone still walks.

  // -- milestone handoff: goal went live in the background ----------------
  // Setup usually does NOT block on wait_for_goal (training takes
  // minutes), so the "serving" moment arrives as a workspace realtime
  // event. Attribute it to the chat via the goal ids its artifact cards
  // carry, then run the same handoff.
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const onAnalystRef = useRef(onAnalyst);
  onAnalystRef.current = onAnalyst;
  const isMobileRef = useRef(isMobile);
  isMobileRef.current = isMobile;
  const handledGoalIds = useRef(new Set());
  useRealtimeRefresh(
    (msg) => msg.event === 'goal.status' && msg.data?.status === 'serving',
    (msg) => {
      const goalId = msg?.data?.goal_id ? String(msg.data.goal_id) : null;
      if (!goalId || handledGoalIds.current.has(goalId)) return;
      if (!chatGoalIdsOf(messagesRef.current).has(goalId)) return;
      handledGoalIds.current.add(goalId);
      if (!onAnalystRef.current || userIsTyping()) return;
      if (!isMobileRef.current) openDock();
      navigate(`/goals/${goalId}`);
    },
  );

  // -- resize --------------------------------------------------------------
  const dragRef = useRef(null);
  const onHandleDown = (e) => {
    e.preventDefault();
    dragRef.current = { startX: e.clientX, startW: width };
    const onMove = (ev) => {
      const d = dragRef.current;
      if (!d) return;
      setWidth(d.startW + (d.startX - ev.clientX));
    };
    const onUp = () => {
      dragRef.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  // On /analyst the page owns the panel: the dock stays mounted but
  // slides off-screen, so the full-page -> docked morph (tour stops,
  // artifact clicks) animates instead of jump-cutting.
  const shown = dockActive && open;

  // The first time the chat ever docks to the side (artifact click, page
  // card click, milestone handoff), point out the way back to full view.
  useEffect(() => { if (shown) maybeDockHint(); }, [shown, maybeDockHint]);

  return (
    <>
      {/* Docked panel. Kept mounted while hidden so panel-local state
          survives pill round-trips and the slide-in can animate. */}
      <Box
        sx={(t) => {
          const s = surfacesFor(t);
          return {
            position: 'fixed',
            top: HEADER_H, right: 0, bottom: 0,
            // Phones: the docked chat fills everything right of the icon
            // rail (the chat is the mobile main surface); larger screens
            // keep the resizable sidecar.
            width: { xs: 'calc(100vw - var(--sidebar-width, 64px))', sm: width },
            maxWidth: '100vw',
            zIndex: 99,   // under the fixed header (100) + sidebar (101)
            display: 'flex',
            flexDirection: 'column',
            visibility: shown ? 'visible' : 'hidden',
            transform: shown ? 'translateX(0)' : 'translateX(calc(100% + 44px))',
            transition: shown
              ? `transform 320ms ${DOCK_EASE}, visibility 0s`
              : `transform 280ms ${DOCK_EASE}, visibility 0s linear 280ms`,
            bgcolor: s.ink,
            borderLeft: `1px solid ${s.border}`,
            boxShadow: t.palette.mode === 'dark'
              ? '-12px 0 34px rgba(0,0,0,0.42)'
              : '-10px 0 30px rgba(40,50,35,0.14)',
          };
        }}
      >
        {/* Drag handle on the left edge (pointless on a phone sheet). */}
        <Box
          onMouseDown={onHandleDown}
          sx={(t) => ({
            position: 'absolute', left: -3, top: 0, bottom: 0, width: 7,
            cursor: 'col-resize', zIndex: 2,
            display: { xs: 'none', sm: 'block' },
            '&:hover': {
              background: `linear-gradient(90deg, transparent, ${
                t.palette.mode === 'dark' ? 'rgba(192,251,110,0.25)' : 'rgba(25,117,99,0.2)'
              })`,
            },
          })}
        />

        {/* Dock header: deliberately nameless - just the spark mark,
            a working pulse, and expand / collapse. */}
        <Stack
          direction="row" alignItems="center" spacing={1}
          sx={(t) => {
            const s = surfacesFor(t);
            return {
              px: 1.75, py: 1,
              borderBottom: `1px solid ${s.border}`,
              bgcolor: s.rail,
              flexShrink: 0,
            };
          }}
        >
          <SparkIcon
            sx={(t) => ({
              fontSize: 15,
              color: t.palette.mode === 'dark' ? (t.brand?.lime || '#c0fb6e') : (t.brand?.teal || t.palette.primary.main),
            })}
          />
          {isLoading && (
            <Box
              sx={(t) => ({
                width: 7, height: 7, borderRadius: '50%',
                bgcolor: t.palette.mode === 'dark' ? (t.brand?.lime || '#c0fb6e') : (t.brand?.teal || t.palette.primary.main),
                animation: `${pulse} 1.4s ease-in-out infinite`,
              })}
            />
          )}
          <Box sx={{ flex: 1 }} />
          <Tooltip title="Expand">
            <IconButton
              size="small"
              onClick={() => navigate('/analyst')}
              aria-label="Expand to full view"
              sx={(t) => ({ color: surfacesFor(t).muted, p: 0.5 })}
            >
              <ExpandIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Collapse">
            <IconButton
              size="small"
              onClick={() => { collapseDock(); maybePillHint(); }}
              aria-label="Collapse"
              sx={(t) => ({ color: surfacesFor(t).muted, p: 0.5 })}
            >
              <CollapseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Stack>

        {/* One-time: shown the first time a navigation docks the chat,
            pointing at the expand control. */}
        {shown && dockHint && (
          <HintBubble
            caret="top-right"
            onClick={() => setDockHint(false)}
            sx={{ top: 46, right: 30, '&::after': { top: -4, right: 18 } }}
          >
            The chat docks here while you explore — expand it back to full
            view anytime.
          </HintBubble>
        )}

        {/* The same conversation, compact. */}
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <AskDataPanel variant="docked" />
        </Box>
      </Box>

      {/* Collapsed pill - the way back in. Filled with the brand color so
          it reads as THE chat entry point, not one more neutral button. */}
      {dockActive && !open && (
          <Box
            role="button"
            tabIndex={0}
            aria-label="Open chat"
            onClick={() => { setPillHint(false); openDock(); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { setPillHint(false); openDock(); } }}
            sx={(t) => {
              const dark = t.palette.mode === 'dark';
              const lime = t.brand?.lime || '#c0fb6e';
              const teal = t.brand?.teal || t.palette.primary.main;
              const fill = dark ? lime : teal;
              return {
                position: 'fixed', right: 20, bottom: 20,
                width: 54, height: 54,
                zIndex: 99,
                borderRadius: '18px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer',
                animation: `${pillIn} 240ms cubic-bezier(0.34, 1.4, 0.44, 1) both`,
                background: dark
                  ? `linear-gradient(135deg, ${lime} 0%, #8ee63e 100%)`
                  : `linear-gradient(135deg, ${teal} 0%, #0d5c4d 100%)`,
                color: dark ? '#101708' : '#ffffff',
                boxShadow: `0 8px 28px ${alpha(fill, 0.45)}, 0 3px 10px rgba(0,0,0,0.35)`,
                transition: 'transform 160ms, box-shadow 160ms',
                '&:hover': {
                  transform: 'translateY(-2px) scale(1.05)',
                  boxShadow: `0 12px 36px ${alpha(fill, 0.55)}, 0 4px 14px rgba(0,0,0,0.4)`,
                },
                // Slow attention ring so the pill stays findable without
                // being loud.
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  inset: 0,
                  borderRadius: '18px',
                  color: fill,
                  animation: `${ringPulse} 2.8s ease-out infinite`,
                  pointerEvents: 'none',
                },
              };
            }}
          >
            <SparkIcon sx={{ fontSize: 24 }} />
            {isLoading && (
              <Box
                sx={(t) => ({
                  position: 'absolute', top: 8, right: 8,
                  width: 8, height: 8, borderRadius: '50%',
                  bgcolor: t.palette.mode === 'dark' ? '#101708' : '#ffffff',
                  animation: `${pulse} 1.4s ease-in-out infinite`,
                })}
              />
            )}
          </Box>
      )}

      {/* One-time: shown the first time the user collapses the chat. */}
      {dockActive && !open && pillHint && (
        <HintBubble
          caret="bottom-right"
          onClick={() => setPillHint(false)}
          sx={{ position: 'fixed', right: 20, bottom: 84, zIndex: 99 }}
        >
          The chat lives here now — reopen it anytime.
        </HintBubble>
      )}
    </>
  );
};

export default AnalystDock;
